import './Btn.css';

export type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type BtnSize = 'sm' | 'md';

/* ── Inline SVG icons — toujours currentColor, s'adaptent à n'importe quel fond ── */
const ICONS: Record<string, React.ReactNode> = {
  plus: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/></svg>
  ),
  trash: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 7h10M8 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2m2 0v9a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  upload: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 13V4m0 0-3 3m3-3 3 3M4 14v.5A2.5 2.5 0 0 0 6.5 17h7a2.5 2.5 0 0 0 2.5-2.5V14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  download: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 3v9m0 0-3-3m3 3 3-3M4 14v.5A2.5 2.5 0 0 0 6.5 17h7a2.5 2.5 0 0 0 2.5-2.5V14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  eye: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10Z" stroke="currentColor" strokeWidth="1.7"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7"/></svg>
  ),
  reset: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 8V4H8M4 8A7 7 0 1 1 5.06 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  number: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7 4 5 16M15 4l-2 12M3.5 8h13M3 12h13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
  ),
  name: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.7"/><path d="M4 18c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
  ),
  image: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="3" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.7"/><circle cx="7.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 14.5 7 11l3 2.5 2-1.5 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  text: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 5h12M10 5v10M7 15h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
  ),
  logo: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="3" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.7"/><circle cx="7.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 14.5 7 11l3 2.5 2-1.5 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  copy: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="7" y="7" width="9" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.7"/><path d="M13 7V5a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 5v6.5A1.5 1.5 0 0 0 5 13h2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
  ),
  back: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12 16 7 10l5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  settings: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7"/><path d="M10 3v1.5M10 15.5V17M3 10h1.5M15.5 10H17M4.93 4.93l1.06 1.06M14 14l1.06 1.06M4.93 15.07l1.06-1.06M14 6l1.06-1.06" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
  ),
};

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: BtnVariant;
  size?: BtnSize;
  icon?: string;
};

export default function Btn({
  variant = 'primary',
  size = 'md',
  icon,
  className = '',
  children,
  ...rest
}: Props) {
  const iconNode = icon ? ICONS[icon] : undefined;

  return (
    <button
      type="button"
      className={`btn btn--${variant} btn--${size} ${className}`.trim()}
      {...rest}
    >
      {iconNode && <span className="btn__icon">{iconNode}</span>}
      {children && <span className="btn__label">{children}</span>}
    </button>
  );
}
